import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'store/store';

import { DateRanges } from 'app/components/DateRange';
import { AsyncSelectOptions } from 'app/types/select-options';

import { DriverCalc } from 'app/services/api/models/DriverCalc';

import { ViolationDetails } from 'app/types/driving-violations';

import { DrivingViolation, SortBy, SortFields } from '../types';
import { drivingViolationsState } from './drivingViolationsState';
import { DocumentType } from 'app/types/document-type';

export const drivingViolationsSlice = createSlice({
  name: 'drivingViolations',
  initialState: drivingViolationsState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    setDriver(state, action: PayloadAction<AsyncSelectOptions | null>) {
      state.driver = action.payload;
    },
    setTerminal(state, action: PayloadAction<AsyncSelectOptions | null>) {
      state.terminal = action.payload;
    },
    setSubset(state, action: PayloadAction<AsyncSelectOptions | null>) {
      state.subset = action.payload;
    },
    setViolation(state, action: PayloadAction<AsyncSelectOptions | null>) {
      state.violation = action.payload;
    },
    setViolations(state, action: PayloadAction<DrivingViolation[]>) {
      state.violations = action.payload;
    },
    setDriverCalcs(state, action: PayloadAction<DriverCalc[]>) {
      state.driverCalcs = action.payload;
    },
    setDateRange(state, action: PayloadAction<DateRanges>) {
      state.dateRange = action.payload;
    },
    setSortBy(state, action: PayloadAction<SortBy>) {
      state.sortBy = action.payload;
    },
    setSortField(state, action: PayloadAction<SortFields>) {
      state.sortField = action.payload;
    },
    setRowsPerPage(state, action: PayloadAction<number>) {
      state.limit = action.payload;
    },
    setPageIndex(state, action: PayloadAction<number>) {
      state.offset = action.payload;
    },
    setTotalRecordCounts(state, action: PayloadAction<number>) {
      state.totalRecordCount = action.payload;
    },
    setRequestPendingTo(state, action: PayloadAction<boolean>) {
      state.isRequestPending = action.payload;
    },
    setToggleFileUploadModal(state, action: PayloadAction<boolean>) {
      state.isToggleFileUploadModal = action.payload;
    },
    setToggleDrivingViolationsModal(state, action: PayloadAction<boolean>) {
      state.isToggleDrivingViolationsModal = action.payload;
    },
    setToggleExportFileModal(state, action: PayloadAction<boolean>) {
      state.isToggleExportFileModal = action.payload;
    },
    setDocumentType(state, action: PayloadAction<DocumentType>) {
      state.documentType = action.payload;
    },
    setViolationExportData(state, action: PayloadAction<DrivingViolation[]>) {
      state.violationsExportData = action.payload;
    },
    setExportDataRequestPendingTo(state, action: PayloadAction<boolean>) {
      state.isExportDataRequestPending = action.payload;
    },
    setDriverViolationDetails(
      state,
      action: PayloadAction<ViolationDetails | null>,
    ) {
      state.driverViolationDetails = action.payload;
    },
    setRefetchDataCount(state, action: PayloadAction<number>) {
      state.refetchDataCount = state.refetchDataCount + action.payload;
    },
    setFileUploadData(
      state,
      action: PayloadAction<{ name: string; value: unknown }>,
    ) {
      state.fileUpload = {
        ...state.fileUpload,
        [action.payload.name]: action.payload.value,
      };
    },
    setDataReset(state) {
      state.refetchDataCount = 0;
      state.isRequestPending = false;
      state.isExportDataRequestPending = false;

      // Toggle Modal
      state.isToggleExportFileModal = false;
      state.isToggleFileUploadModal = false;
      state.isToggleDrivingViolationsModal = false;

      // Filters
      state.driver = null;
      state.terminal = null;
      state.subset = null;
      state.violation = null;
      state.dateRange = drivingViolationsState.dateRange;

      // Sorting
      state.sortBy = drivingViolationsState.sortBy;
      state.sortField = drivingViolationsState.sortField;

      // Pagination
      state.limit = 10;
      state.offset = 0;
      state.totalRecordCount = 0;

      // File upload
      state.fileUpload.file = null;
      state.fileUpload.note = '';
      state.fileUpload.userId = '';
      state.fileUpload.driverViolationId = '';
      state.fileUpload.isRequestPending = false;

      // Data source
      state.violations = [];
      state.driverCalcs = [];
      state.driverViolationDetails = null;

      // Export file data source
      state.documentType = 'pdf';
      state.violationsExportData = [];
    },
    setFileData(state) {
      state.fileUpload.file = null;
      state.fileUpload.note = '';
    },
    setDocumentUploadedFromCompModel(state, action: PayloadAction<boolean>) {
      state.documentUploadedFromCompModel = action.payload;
    },
  },
});

/* ------- ACTION CREATORS ------- */
export const { actions: drivingViolationsActions } = drivingViolationsSlice;

/* ------- STATE SELECTORS ------- */
export const selectDrivingViolations = (state: RootState) => state.violations;

export default drivingViolationsSlice.reducer;
